import { useState, useRef, useCallback, useEffect } from 'react';
import {
  Container,
  Title,
  Text,
  Paper,
  Button,
  Stack,
  Group,
  FileButton,
  TextInput,
  NumberInput,
  Modal,
  Card,
  Badge,
  ActionIcon,
  Grid,
  Select,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconUpload,
  IconTrash,
  IconDownload,
  IconInfoCircle,
  IconCheck,
  IconX,
  IconSearch,
  IconChevronUp,
  IconChevronDown,
  IconList,
  IconEdit,
  IconLetterCase,
  IconSquareCheck,
  IconPhoto,
  IconDeviceFloppy,
  IconFolderOpen,
} from '@tabler/icons-react';
import { PDFDocument, rgb, PDFName, PDFString } from 'pdf-lib';
import * as pdfjsLib from 'pdfjs-dist';
import { useTranslation } from '../contexts/hooks';
import { loadFieldSizePreferences, saveFieldSizePreferences } from '../utils/storage/localStorage';
import { addImageToHistory } from '../utils/storage/imageHistory';
import { ImageHistoryModal } from '../components/common/ImageHistoryModal';
import { SaveProjectModal } from '../components/common/SaveProjectModal';
import { SavedProjectsModal } from '../components/common/SavedProjectsModal';
import { type SavedPDFProject, base64ToFile, getSavedProjects } from '../utils/storage/pdfProjects';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

interface PDFField {
  id: string;
  name: string;
  type: 'text' | 'checkbox' | 'image';
  x: number;
  y: number;
  width: number;
  height: number;
  pageIndex: number;
  isExisting?: boolean; // To differentiate between user-created and existing fields
  fontSize?: number; // Font size for text fields
  checkboxSize?: number; // Size for checkbox fields
  imageData?: string; // Base64 encoded image data
  imageName?: string; // Original image filename
}

interface PDFEditorPageProps {
  onMobileMenuStateChange: (state: {
    pdfFile?: File | null;
    isTopSectionExpanded: boolean;
    onToggleTopSection: () => void;
  }) => void;
}

export function PDFEditorPage({ onMobileMenuStateChange }: PDFEditorPageProps) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 768px)');
  
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pdfDoc, setPdfDoc] = useState<PDFDocument | null>(null);
  const [pdfJsDoc, setPdfJsDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [fields, setFields] = useState<PDFField[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawingMode, setDrawingMode] = useState<'text' | 'checkbox' | 'image'>('text');
  const [fontSize, setFontSize] = useState(() => loadFieldSizePreferences().fontSize); // Load from localStorage
  const [checkboxSize, setCheckboxSize] = useState(() => loadFieldSizePreferences().checkboxSize); // Load from localStorage
  const [startPos, setStartPos] = useState<{ x: number; y: number } | null>(null);
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [currentField, setCurrentField] = useState<PDFField | null>(null);
  const [fieldName, setFieldName] = useState('');
  const [editFontSize, setEditFontSize] = useState(() => loadFieldSizePreferences().fontSize);
  const [editCheckboxSize, setEditCheckboxSize] = useState(() => loadFieldSizePreferences().checkboxSize);
  const [isEditMode, setIsEditMode] = useState(false);
  const [scale, setScale] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [existingFields, setExistingFields] = useState<PDFField[]>([]);
  const [hasDeletedFields, setHasDeletedFields] = useState(false);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isTopSectionExpanded, setIsTopSectionExpanded] = useState(true);
  const [showFieldListOnMobile, setShowFieldListOnMobile] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [pendingImageData, setPendingImageData] = useState<string | null>(null);
  const [showImageHistoryModal, setShowImageHistoryModal] = useState(false);
  
  // Project management state
  const [showSaveProjectModal, setShowSaveProjectModal] = useState(false);
  const [showSavedProjectsModal, setShowSavedProjectsModal] = useState(false);
  const [currentProjectName, setCurrentProjectName] = useState<string | null>(null);
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(null);
  const [hasSavedProjects, setHasSavedProjects] = useState(false);
  
  // Drag and move state
  const [isDragging, setIsDragging] = useState(false);
  const [draggedField, setDraggedField] = useState<PDFField | null>(null);
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number } | null>(null);
  const [hoveredField, setHoveredField] = useState<PDFField | null>(null);
  const [canvasCursor, setCanvasCursor] = useState<string>('crosshair');
  
  // Resize state
  const [isResizing, setIsResizing] = useState(false);
  const [resizeField, setResizeField] = useState<PDFField | null>(null);
  const [resizeHandle, setResizeHandle] = useState<string | null>(null);
  const [resizeStartPos, setResizeStartPos] = useState<{ x: number; y: number } | null>(null);
  const [resizeStartField, setResizeStartField] = useState<PDFField | null>(null);
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const overlayCanvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const renderTaskRef = useRef<pdfjsLib.RenderTask | null>(null);
  const fieldNameInputRef = useRef<HTMLInputElement>(null);
  
  // Refs to store latest state for stable access in drawFieldOverlays
  const fieldsRef = useRef(fields);
  const existingFieldsRef = useRef(existingFields);
  const currentPageRef = useRef(currentPage);
  const scaleRef = useRef(scale);
  
  // Keep refs updated
  useEffect(() => {
    fieldsRef.current = fields;
  }, [fields]);
  
  useEffect(() => {
    existingFieldsRef.current = existingFields;
  }, [existingFields]);
  
  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);
  
  useEffect(() => {
    scaleRef.current = scale;
  }, [scale]);

  // Wrapper functions to save field size preferences to localStorage
  const updateFontSize = useCallback((newFontSize: number) => {
    setFontSize(newFontSize);
    const currentPrefs = loadFieldSizePreferences();
    saveFieldSizePreferences({ ...currentPrefs, fontSize: newFontSize });
  }, []);

  const updateCheckboxSize = useCallback((newCheckboxSize: number) => {
    setCheckboxSize(newCheckboxSize);
    const currentPrefs = loadFieldSizePreferences();
    saveFieldSizePreferences({ ...currentPrefs, checkboxSize: newCheckboxSize });
  }, []);

  // Auto-focus on field name input when modal opens
  useEffect(() => {
    if (showFieldModal && fieldNameInputRef.current) {
      // Multiple timing approaches to ensure focus works reliably
      // 1. Immediate focus attempt
      fieldNameInputRef.current?.focus();
      
      // 2. Small delay for DOM updates
      const timeoutId1 = setTimeout(() => {
        fieldNameInputRef.current?.focus();
        fieldNameInputRef.current?.select();
      }, 50);
      
      // 3. Longer delay as fallback for complex rendering
      const timeoutId2 = setTimeout(() => {
        fieldNameInputRef.current?.focus();
        fieldNameInputRef.current?.select();
      }, 100);
      
      // 4. Final fallback using requestAnimationFrame
      const rafId = requestAnimationFrame(() => {
        fieldNameInputRef.current?.focus();
        fieldNameInputRef.current?.select();
      });
      
      return () => {
        clearTimeout(timeoutId1);
        clearTimeout(timeoutId2);
        cancelAnimationFrame(rafId);
      };
    }
  }, [showFieldModal]);

  // Update mobile menu state when relevant state changes
  useEffect(() => {
    onMobileMenuStateChange({
      pdfFile,
      isTopSectionExpanded,
      onToggleTopSection: () => setIsTopSectionExpanded(!isTopSectionExpanded)
    });
  }, [pdfFile, isTopSectionExpanded, onMobileMenuStateChange]);

  // Check for saved projects on mount
  useEffect(() => {
    const checkSavedProjects = async () => {
      try {
        const savedProjects = await getSavedProjects();
        setHasSavedProjects(savedProjects.length > 0);
      } catch (error) {
        console.error('Failed to check saved projects:', error);
        setHasSavedProjects(false);
      }
    };
    checkSavedProjects();
  }, []);

  const drawResizeHandles = useCallback((ctx: CanvasRenderingContext2D, field: PDFField, currentScale: number) => {
    const x = field.x * currentScale;
    const y = field.y * currentScale;
    const width = field.width * currentScale;
    const height = field.height * currentScale;
    
    const handleSize = 8; // Handle size in canvas pixels
    const halfHandle = handleSize / 2;
    
    // Handle positions
    const handles = [
      { x: x - halfHandle, y: y - halfHandle }, // nw
      { x: x + width / 2 - halfHandle, y: y - halfHandle }, // n
      { x: x + width - halfHandle, y: y - halfHandle }, // ne
      { x: x + width - halfHandle, y: y + height / 2 - halfHandle }, // e
      { x: x + width - halfHandle, y: y + height - halfHandle }, // se
      { x: x + width / 2 - halfHandle, y: y + height - halfHandle }, // s
      { x: x - halfHandle, y: y + height - halfHandle }, // sw
      { x: x - halfHandle, y: y + height / 2 - halfHandle }, // w
    ];
    
    // Draw handles
    ctx.fillStyle = '#fff';
    ctx.strokeStyle = '#333';
    ctx.lineWidth = 1;
    
    handles.forEach(handle => {
      ctx.fillRect(handle.x, handle.y, handleSize, handleSize);
      ctx.strokeRect(handle.x, handle.y, handleSize, handleSize);
    });
  }, []);

  const drawField = useCallback((ctx: CanvasRenderingContext2D, field: PDFField, currentScale: number) => {
    const x = field.x * currentScale;
    const y = field.y * currentScale;
    const width = field.width * currentScale;
    const height = field.height * currentScale;
    
    // Check if this field is selected
    const isSelected = selectedFieldId === field.id;
    
    // Handle image fields differently
    if (field.type === 'image' && field.imageData) {
      // Draw the image
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, x, y, width, height);
        
        // Draw border for image fields
        ctx.strokeStyle = isSelected ? '#e03131' : '#adb5bd'; // red for selected, gray for normal
        ctx.lineWidth = isSelected ? 3 : 1;
        ctx.strokeRect(x, y, width, height);
        
        // Draw image name if available
        if (field.imageName) {
          ctx.fillStyle = '#333';
          ctx.font = '12px Arial';
          ctx.textAlign = 'left';
          ctx.textBaseline = 'alphabetic';
          ctx.fillText(field.imageName, x + 2, y - 4);
        }
        
        // Add selected indicator
        if (isSelected) {
          ctx.fillStyle = '#333';
          ctx.font = 'bold 10px Arial';
          ctx.fillText('(sélectionné)', x + 2, y - 16);
          
          // Draw resize handles for selected image field
          drawResizeHandles(ctx, field, currentScale);
        }
      };
      img.src = field.imageData;
      return;
    }
    
    // Different colors for existing vs new fields
    const isExisting = field.isExisting;
    const textColor = isExisting ? '#fd7e14' : '#339af0'; // orange for existing, blue for new
    const checkboxColor = isExisting ? '#fd7e14' : '#51cf66'; // orange for existing, green for new
    
    // Draw field box
    ctx.strokeStyle = field.type === 'text' ? textColor : checkboxColor;
    ctx.lineWidth = isExisting ? 3 : 2; // Thicker border for existing fields
    
    // Add extra highlighting for selected field
    if (isSelected) {
      ctx.lineWidth = 4;
      ctx.shadowColor = field.type === 'text' ? textColor : checkboxColor;
      ctx.shadowBlur = 8;
    }
    
    ctx.strokeRect(x, y, width, height);
    
    // Reset shadow for other elements
    if (isSelected) {
      ctx.shadowBlur = 0;
    }
    
    // Fill with semi-transparent color
    let fillColor = field.type === 'text' 
      ? (isExisting ? 'rgba(253, 126, 20, 0.1)' : 'rgba(51, 154, 240, 0.1)')
      : (isExisting ? 'rgba(253, 126, 20, 0.1)' : 'rgba(81, 207, 102, 0.1)');
    
    // Make selected field more visible
    if (isSelected) {
      fillColor = field.type === 'text' 
        ? (isExisting ? 'rgba(253, 126, 20, 0.3)' : 'rgba(51, 154, 240, 0.3)')
        : (isExisting ? 'rgba(253, 126, 20, 0.3)' : 'rgba(81, 207, 102, 0.3)');
    }
    
    ctx.fillStyle = fillColor;
    ctx.fillRect(x, y, width, height);
    
    // Draw field name
    ctx.fillStyle = '#333';
    ctx.font = '12px Arial';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(field.name, x + 2, y - 4);
    
    // Add indicator for existing fields
    if (isExisting) {
      ctx.fillStyle = '#fd7e14';
      ctx.font = '10px Arial';
      ctx.fillText('(existant)', x + 2, y - 16);
    }
    
    // Add selected indicator
    if (isSelected) {
      ctx.fillStyle = '#333';
      ctx.font = 'bold 10px Arial';
      ctx.fillText('(sélectionné)', x + 2, y - (isExisting ? 28 : 16));
      
      // Draw resize handles for selected field
      drawResizeHandles(ctx, field, currentScale);
    }
  }, [selectedFieldId, drawResizeHandles]);

  const extractExistingFields = useCallback(async (pdfDocument: PDFDocument) => {
    try {
      const form = pdfDocument.getForm();
      const existingFieldsData: PDFField[] = [];
      
      // Get all form fields
      const formFields = form.getFields();
      
      console.log('Found form fields:', formFields.length);
      
      formFields.forEach((field, index) => {
        const fieldName = field.getName();
        console.log('Processing field:', fieldName, 'Type:', field.constructor.name);
        
        const widgets = field.acroField.getWidgets();
        
        widgets.forEach((widget, widgetIndex) => {
          const rect = widget.getRectangle();
          
          // Get page index - simplified approach as widget API is complex
          let pageIndex = 0;
          try {
            // Try to get page information from the widget's annotation dictionary
            // For now, we'll default to page 0 and let the user correct if needed
            // In a real implementation, we would need to properly traverse the PDF structure
            pageIndex = 0;
          } catch (error) {
            console.warn('Could not determine page index for field:', fieldName, error);
            pageIndex = 0;
          }
          
          if (rect) {
            const pages = pdfDocument.getPages();
            const pageSize = pages[pageIndex].getSize();
            
            // Convert PDF coordinates to canvas coordinates
            const x = rect.x;
            const y = pageSize.height - rect.y - rect.height; // Convert from bottom-left to top-left origin
            const width = rect.width;
            const height = rect.height;
            
            // Determine field type - check multiple ways
            let fieldType: 'text' | 'checkbox' = 'text';
            const constructorName = field.constructor.name;
            
            if (constructorName === 'PDFCheckBox' || 
                constructorName.includes('CheckBox') ||
                constructorName.includes('Button')) {
              fieldType = 'checkbox';
            }
            
            console.log('Adding existing field:', {
              name: fieldName,
              type: fieldType,
              x, y, width, height,
              pageIndex
            });
            
            existingFieldsData.push({
              id: `existing-${index}-${widgetIndex}`,
              name: fieldName,
              type: fieldType,
              x,
              y,
              width,
              height,
              pageIndex,
              isExisting: true,
              checkboxSize: fieldType === 'checkbox' ? Math.round(width) : undefined, // Set checkboxSize for checkbox fields (rounded to integer)
            });
          }
        });
      });
      
      console.log('Extracted existing fields:', existingFieldsData);
      setExistingFields(existingFieldsData);
    } catch (error) {
      console.error('Error extracting existing fields:', error);
      setExistingFields([]);
    }
  }, []);

  // Function to draw field overlays on the overlay canvas
  const drawFieldOverlays = useCallback(() => {
    const overlayCanvas = overlayCanvasRef.current;
    if (!overlayCanvas) return;
    
    const ctx = overlayCanvas.getContext('2d');
    if (!ctx) return;
    
    // Clear the overlay canvas
    ctx.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);
    
    // Use refs to get latest state without causing recreation of this function
    const currentFields = fieldsRef.current;
    const currentExistingFields = existingFieldsRef.current;
    const currentPageNum = currentPageRef.current;
    const currentScale = scaleRef.current;
    
    // Draw existing fields for this page
    const pageExistingFields = currentExistingFields.filter(f => f.pageIndex === currentPageNum);
    pageExistingFields.forEach(field => {
      drawField(ctx, field, currentScale);
    });
    
    // Draw user-created fields for this page
    const pageFields = currentFields.filter(f => f.pageIndex === currentPageNum);
    pageFields.forEach(field => {
      drawField(ctx, field, currentScale);
    });
  }, [drawField]); // Only depend on drawField, not on the state values

  // Function to draw only the preview on the overlay canvas
  const drawPreview = useCallback((start: { x: number; y: number }, end: { x: number; y: number }) => {
    const overlayCanvas = overlayCanvasRef.current;
    if (!overlayCanvas) return;
    
    const ctx = overlayCanvas.getContext('2d');
    if (!ctx) return;
    
    // Clear the overlay canvas
    ctx.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);
    
    // First, redraw all existing field overlays to maintain them during preview
    const pageExistingFields = existingFields.filter(f => f.pageIndex === currentPage);
    pageExistingFields.forEach(field => {
      drawField(ctx, field, scale);
    });
    
    // Then draw user-created fields for this page
    const pageFields = fields.filter(f => f.pageIndex === currentPage);
    pageFields.forEach(field => {
      drawField(ctx, field, scale);
    });
    
    // Finally, draw the preview on top
    const x = Math.min(start.x, end.x) * scale;
    const y = Math.min(start.y, end.y) * scale;
    const width = Math.abs(end.x - start.x) * scale;
    const height = Math.abs(end.y - start.y) * scale;
    
    // Draw preview rectangle
    let strokeColor, fillColor, previewText;
    
    if (drawingMode === 'image') {
      strokeColor = '#e03131'; // red for images
      fillColor = 'rgba(224, 49, 49, 0.2)';
      previewText = pendingImageData ? t('editor.field.type.image.short') : t('editor.image.select');
    } else if (drawingMode === 'text') {
      strokeColor = '#339af0'; // blue for text
      fillColor = 'rgba(51, 154, 240, 0.2)';
      previewText = t('editor.field.type.text.short');
    } else {
      strokeColor = '#51cf66'; // green for checkbox
      fillColor = 'rgba(81, 207, 102, 0.2)';
      previewText = t('editor.field.type.checkbox.short');
    }
    
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 5]); // Dashed line for preview
    ctx.strokeRect(x, y, width, height);
    
    // Fill with semi-transparent color or draw image preview
    if (drawingMode === 'image' && pendingImageData) {
      // Draw preview image
      const img = new Image();
      img.onload = () => {
        ctx.save();
        ctx.globalAlpha = 0.7; // Make it semi-transparent for preview
        ctx.drawImage(img, x, y, width, height);
        ctx.restore();
        
        // Still draw the dashed border on top
        ctx.strokeStyle = strokeColor;
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        ctx.strokeRect(x, y, width, height);
        ctx.setLineDash([]);
      };
      img.src = pendingImageData;
    } else {
      ctx.fillStyle = fillColor;
      ctx.fillRect(x, y, width, height);
    }
    
    // Draw preview text
    ctx.fillStyle = '#333';
    ctx.font = '12px Arial';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.setLineDash([]); // Reset line dash for text
    ctx.fillText(previewText, x + 2, y - 4);
  }, [drawingMode, scale, existingFields, fields, currentPage, drawField, pendingImageData, t]);

  // Function to clear the preview and redraw field overlays
  const clearPreview = useCallback(() => {
    const overlayCanvas = overlayCanvasRef.current;
    if (!overlayCanvas) return;
    
    const ctx = overlayCanvas.getContext('2d');
    if (!ctx) return;
    
    // Clear the overlay canvas
    ctx.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);
    
    // Redraw all existing field overlays
    const pageExistingFields = existingFields.filter(f => f.pageIndex === currentPage);
    pageExistingFields.forEach(field => {
      drawField(ctx, field, scale);
    });
    
    // Redraw user-created fields for this page
    const pageFields = fields.filter(f => f.pageIndex === currentPage);
    pageFields.forEach(field => {
      drawField(ctx, field, scale);
    });
  }, [existingFields, fields, currentPage, scale, drawField]);
  // Function to render PDF page and setup overlay canvas
  const renderPDFPage = useCallback(async (pdfJsDocument: pdfjsLib.PDFDocumentProxy, pageIndex: number) => {
    if (!pdfJsDocument) return;
    
    try {
      const page = await pdfJsDocument.getPage(pageIndex + 1); // PDF.js uses 1-based indexing
      const canvas = canvasRef.current;
      const overlayCanvas = overlayCanvasRef.current;
      if (!canvas || !overlayCanvas) return;
      
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      
      // Calculate scale to fit container - make PDF bigger
      const container = containerRef.current;
      if (!container) return;
      
      const containerWidth = container.clientWidth - 40; // padding
      const viewport = page.getViewport({ scale: 1 });
      const scaleX = containerWidth / viewport.width;
      const scaleY = (window.innerHeight * 0.9) / viewport.height;
      const newScale = Math.min(scaleX, scaleY, 2.0); // Allow up to 2.0x scale for bigger preview
      
      setScale(newScale);
      
      // Set canvas size for both canvases
      const scaledViewport = page.getViewport({ scale: newScale });
      canvas.width = scaledViewport.width;
      canvas.height = scaledViewport.height;
      overlayCanvas.width = scaledViewport.width;
      overlayCanvas.height = scaledViewport.height;
      
      // Clear main canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Cancel any previous render operations
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
      }
      
      // Render PDF page on main canvas
      const renderContext = {
        canvasContext: ctx,
        viewport: scaledViewport,
      };
      
      const renderTask = page.render(renderContext);
      renderTaskRef.current = renderTask;
      
      await renderTask.promise;
      
      // After PDF is rendered, draw field overlays if we have existing fields
      if (existingFields.length > 0) {
        // Small delay to ensure the PDF is fully rendered
        setTimeout(() => {
          drawFieldOverlays();
        }, 100);
      }
      
    } catch (error) {
      if (error instanceof Error && error.name !== 'RenderingCancelledException') {
        console.error('Error rendering page:', error);
      }
    }
  }, [existingFields.length, drawFieldOverlays]);

  // Effect to render the page when PDF is loaded
  useEffect(() => {
    if (pdfJsDoc) {
      renderPDFPage(pdfJsDoc, currentPage);
    }
  }, [pdfJsDoc, currentPage, renderPDFPage, existingFields.length]);

  // Effect to draw field overlays when fields, scale changes
  useEffect(() => {
    if (pdfJsDoc && scale > 0) {
      drawFieldOverlays();
    }
  // Note: drawFieldOverlays now uses refs for stable access to current state
  }, [fields, existingFields, currentPage, scale, pdfJsDoc, drawFieldOverlays]);

  // Clear selection when field is deleted
  useEffect(() => {
    if (selectedFieldId) {
      const allFields = [...fields, ...existingFields];
      const fieldExists = allFields.find(f => f.id === selectedFieldId);
      if (!fieldExists) {
        setSelectedFieldId(null);
      }
    }
  }, [fields, existingFields, selectedFieldId]);

  // Cleanup render task on unmount
  useEffect(() => {
    return () => {
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
      }
    };
  }, []);

  // Add document-level event listeners for proper resize handling
  useEffect(() => {
    const handleDocumentMouseUp = () => {
      if (isResizing) {
        setIsResizing(false);
        setResizeField(null);
        setResizeHandle(null);
        setResizeStartPos(null);
        setResizeStartField(null);
        setCanvasCursor('crosshair');
      }
    };

    const handleDocumentTouchEnd = () => {
      if (isResizing) {
        setIsResizing(false);
        setResizeField(null);
        setResizeHandle(null);
        setResizeStartPos(null);
        setResizeStartField(null);
        setCanvasCursor('crosshair');
      }
    };

    // Add event listeners to document to catch mouse/touch up events outside canvas
    document.addEventListener('mouseup', handleDocumentMouseUp);
    document.addEventListener('touchend', handleDocumentTouchEnd);

    return () => {
      document.removeEventListener('mouseup', handleDocumentMouseUp);
      document.removeEventListener('touchend', handleDocumentTouchEnd);
    };
  }, [isResizing]);

  // Function to handle image selection
  const handleImageSelect = useCallback((file: File | null) => {
    if (!file) {
      return; // User cancelled file selection
    }
    
    if (!file.type.startsWith('image/')) {
      notifications.show({
        title: 'Erreur',
        message: 'Veuillez sélectionner un fichier image valide (PNG, JPEG, etc.)',
        color: 'red',
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      if (result) {
        setSelectedImage(file);
        setPendingImageData(result);
        setDrawingMode('image');
        notifications.show({
          title: 'Image sélectionnée',
          message: 'Dessinez maintenant une zone sur le PDF pour positionner l\'image',
          color: 'blue',
        });
      }
    };
    reader.readAsDataURL(file);
  }, []);

  // Function to handle image selection from history
  const handleImageSelectFromHistory = useCallback((imageData: string, imageName: string) => {
    setSelectedImage({ name: imageName } as File);
    setPendingImageData(imageData);
    setDrawingMode('image');
    notifications.show({
      title: 'Image sélectionnée',
      message: 'Dessinez maintenant une zone sur le PDF pour positionner l\'image',
      color: 'blue',
    });
  }, []);

  // Function to open image history modal
  const handleOpenImageHistory = useCallback(() => {
    setShowImageHistoryModal(true);
  }, []);

  // Callback to refresh saved projects availability
  const refreshSavedProjectsState = useCallback(async () => {
    try {
      const savedProjects = await getSavedProjects();
      setHasSavedProjects(savedProjects.length > 0);
    } catch (error) {
      console.error('Failed to refresh saved projects state:', error);
      setHasSavedProjects(false);
    }
  }, []);

  // Project management handlers
  const handleSaveProject = useCallback(() => {
    if (!pdfFile) {
      notifications.show({
        title: 'Erreur',
        message: 'Aucun PDF chargé',
        color: 'red',
      });
      return;
    }
    setShowSaveProjectModal(true);
  }, [pdfFile]);

  const handleLoadProjects = useCallback(() => {
    setShowSavedProjectsModal(true);
  }, []);

  const loadPDF = useCallback(async (file: File | null, preserveProjectState = false) => {
    if (!file) return;
    
    setIsLoading(true);
    try {
      const arrayBuffer = await file.arrayBuffer();
      
      // Load with pdf-lib for form field creation
      const pdfLibDoc = await PDFDocument.load(arrayBuffer);
      setPdfDoc(pdfLibDoc);
      
      // Extract existing form fields
      await extractExistingFields(pdfLibDoc);
      
      // Load with PDF.js for rendering
      const pdfJsDocument = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      setPdfJsDoc(pdfJsDocument);
      
      setPdfFile(file);
      setTotalPages(pdfJsDocument.numPages);
      setCurrentPage(0);
      setFields([]);
      setSelectedFieldId(null);
      setSearchTerm('');
      setHasDeletedFields(false);
      
      // Clear project state only when loading a new PDF (not when loading a project)
      if (!preserveProjectState) {
        setCurrentProjectName(null);
        setCurrentProjectId(null);
      }
      
      // Auto-collapse the top section when PDF is loaded
      setIsTopSectionExpanded(false);
      // extractExistingFields is called above - don't reset existing fields count here
      
    } catch (error) {
      console.error('Error loading PDF:', error);
    } finally {
      setIsLoading(false);
    }
  }, [extractExistingFields]);

  const handleLoadProject = useCallback(async (project: SavedPDFProject) => {
    try {
      setIsLoading(true);
      
      // Convert base64 back to File
      const pdfFile = base64ToFile(project.pdfData, project.originalFileName);
      
      // Load the PDF using existing loadPDF function
      await loadPDF(pdfFile, true);
      
      // Restore the project state
      setFields(project.fields);
      setExistingFields(project.existingFields);
      setCurrentPage(project.currentPage);
      setCurrentProjectName(project.name);
      setCurrentProjectId(project.id);
      
      // Refresh saved projects availability
      await refreshSavedProjectsState();
      
      notifications.show({
        title: 'Projet chargé',
        message: `Le projet "${project.name}" a été chargé avec succès`,
        color: 'green',
      });
    } catch (error) {
      console.error('Error loading project:', error);
      notifications.show({
        title: 'Erreur',
        message: 'Impossible de charger le projet',
        color: 'red',
      });
    } finally {
      setIsLoading(false);
    }
  }, [loadPDF, refreshSavedProjectsState]);

  // Helper function to get coordinates from mouse or touch event
  const getCoordinates = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    
    const rect = canvas.getBoundingClientRect();
    let clientX: number, clientY: number;
    
    if ('touches' in e) {
      // Touch event
      if (e.touches.length > 0) {
        clientX = e.touches[0].clientX;
        clientY = e.touches[0].clientY;
      } else if (e.changedTouches.length > 0) {
        clientX = e.changedTouches[0].clientX;
        clientY = e.changedTouches[0].clientY;
      } else {
        return null;
      }
    } else {
      // Mouse event
      clientX = e.clientX;
      clientY = e.clientY;
    }
    
    const x = (clientX - rect.left) / scale;
    const y = (clientY - rect.top) / scale;
    
    return { x, y };
  };

  // Helper function to check if coordinates are within a field
  const getFieldAtCoordinates = (x: number, y: number): PDFField | null => {
    // Check user-created fields first (they appear on top)
    const pageFields = fields.filter(f => f.pageIndex === currentPage);
    for (let i = pageFields.length - 1; i >= 0; i--) {
      const field = pageFields[i];
      if (x >= field.x && x <= field.x + field.width &&
          y >= field.y && y <= field.y + field.height) {
        return field;
      }
    }
    
    // Then check existing fields
    const pageExistingFields = existingFields.filter(f => f.pageIndex === currentPage);
    for (let i = pageExistingFields.length - 1; i >= 0; i--) {
      const field = pageExistingFields[i];
      if (x >= field.x && x <= field.x + field.width &&
          y >= field.y && y <= field.y + field.height) {
        return field;
      }
    }
    
    return null;
  };

  // Helper function to detect resize handles
  const getResizeHandle = (x: number, y: number, field: PDFField): string | null => {
    if (!field || selectedFieldId !== field.id) return null; // Only show handles for selected fields
    
    const handleSize = 8 / scale; // Handle size in PDF coordinates
    const tolerance = handleSize / 2;
    
    const left = field.x;
    const right = field.x + field.width;
    const top = field.y;
    const bottom = field.y + field.height;
    const centerX = field.x + field.width / 2;
    const centerY = field.y + field.height / 2;
    
    // Corner handles
    if (Math.abs(x - left) <= tolerance && Math.abs(y - top) <= tolerance) return 'nw';
    if (Math.abs(x - right) <= tolerance && Math.abs(y - top) <= tolerance) return 'ne';
    if (Math.abs(x - left) <= tolerance && Math.abs(y - bottom) <= tolerance) return 'sw';
    if (Math.abs(x - right) <= tolerance && Math.abs(y - bottom) <= tolerance) return 'se';
    
    // Edge handles
    if (Math.abs(x - centerX) <= tolerance && Math.abs(y - top) <= tolerance) return 'n';
    if (Math.abs(x - centerX) <= tolerance && Math.abs(y - bottom) <= tolerance) return 's';
    if (Math.abs(x - left) <= tolerance && Math.abs(y - centerY) <= tolerance) return 'w';
    if (Math.abs(x - right) <= tolerance && Math.abs(y - centerY) <= tolerance) return 'e';
    
    return null;
  };

  // Helper function to get cursor style for resize handle
  const getResizeCursor = (handle: string): string => {
    switch (handle) {
      case 'nw':
      case 'se':
        return 'nw-resize';
      case 'ne':
      case 'sw':
        return 'ne-resize';
      case 'n':
      case 's':
        return 'ns-resize';
      case 'e':
      case 'w':
        return 'ew-resize';
      default:
        return 'grab';
    }
  };

  const handleCanvasMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const coords = getCoordinates(e);
    if (!coords) return;
    
    // Check if clicking on an existing field
    const fieldAtPosition = getFieldAtCoordinates(coords.x, coords.y);
    
    if (fieldAtPosition) {
      // First, ensure the field is selected
      const wasFieldSelected = selectedFieldId === fieldAtPosition.id;
      if (!wasFieldSelected) {
        setSelectedFieldId(fieldAtPosition.id);
      }
      
      // Check if clicking on a resize handle (works for both newly selected and already selected fields)
      const handle = getResizeHandle(coords.x, coords.y, fieldAtPosition);
      
      if (handle) {
        // Start resizing immediately
        setIsResizing(true);
        setResizeField(fieldAtPosition);
        setResizeHandle(handle);
        setResizeStartPos(coords);
        setResizeStartField({ ...fieldAtPosition });
        setCanvasCursor(getResizeCursor(handle));
      } else {
        // Start dragging the field
        setIsDragging(true);
        setDraggedField(fieldAtPosition);
        setCanvasCursor('grabbing');
        setDragOffset({
          x: coords.x - fieldAtPosition.x,
          y: coords.y - fieldAtPosition.y
        });
      }
    } else {
      // Start drawing a new field
      setStartPos(coords);
      setIsDrawing(true);
      setSelectedFieldId(null);
    }
  };

  const handleCanvasMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const coords = getCoordinates(e);
    if (!coords) return;
    
    if (isResizing && resizeField && resizeHandle && resizeStartPos && resizeStartField) {
      // Handle field resizing
      const deltaX = coords.x - resizeStartPos.x;
      const deltaY = coords.y - resizeStartPos.y;
      
      let newX = resizeStartField.x;
      let newY = resizeStartField.y;
      let newWidth = resizeStartField.width;
      let newHeight = resizeStartField.height;
      
      // Calculate new dimensions based on handle
      switch (resizeHandle) {
        case 'nw':
          newX = resizeStartField.x + deltaX;
          newY = resizeStartField.y + deltaY;
          newWidth = resizeStartField.width - deltaX;
          newHeight = resizeStartField.height - deltaY;
          break;
        case 'n':
          newY = resizeStartField.y + deltaY;
          newHeight = resizeStartField.height - deltaY;
          break;
        case 'ne':
          newY = resizeStartField.y + deltaY;
          newWidth = resizeStartField.width + deltaX;
          newHeight = resizeStartField.height - deltaY;
          break;
        case 'e':
          newWidth = resizeStartField.width + deltaX;
          break;
        case 'se':
          newWidth = resizeStartField.width + deltaX;
          newHeight = resizeStartField.height + deltaY;
          break;
        case 's':
          newHeight = resizeStartField.height + deltaY;
          break;
        case 'sw':
          newX = resizeStartField.x + deltaX;
          newWidth = resizeStartField.width - deltaX;
          newHeight = resizeStartField.height + deltaY;
          break;
        case 'w':
          newX = resizeStartField.x + deltaX;
          newWidth = resizeStartField.width - deltaX;
          break;
      }
      
      // Enforce minimum size
      const minSize = 10;
      if (newWidth < minSize) {
        if (resizeHandle.includes('w')) {
          newX = resizeStartField.x + resizeStartField.width - minSize;
        }
        newWidth = minSize;
      }
      if (newHeight < minSize) {
        if (resizeHandle.includes('n')) {
          newY = resizeStartField.y + resizeStartField.height - minSize;
        }
        newHeight = minSize;
      }
      
      // Update field
      const updatedField = { ...resizeField, x: newX, y: newY, width: newWidth, height: newHeight };
      
      if (resizeField.isExisting) {
        setExistingFields(prev => 
          prev.map(f => f.id === resizeField.id ? updatedField : f)
        );
      } else {
        setFields(prev => 
          prev.map(f => f.id === resizeField.id ? updatedField : f)
        );
      }
      
      // Update the resizeField reference for continued resizing
      setResizeField(updatedField);
      
      // Manually redraw overlays during resize
      drawFieldOverlays();
    } else if (isDragging && draggedField && dragOffset) {
      // Handle field dragging
      const newX = coords.x - dragOffset.x;
      const newY = coords.y - dragOffset.y;
      
      // Update field position
      const updatedField = { ...draggedField, x: newX, y: newY };
      
      if (draggedField.isExisting) {
        setExistingFields(prev => 
          prev.map(f => f.id === draggedField.id ? updatedField : f)
        );
      } else {
        setFields(prev => 
          prev.map(f => f.id === draggedField.id ? updatedField : f)
        );
      }
      
      // Update the draggedField reference for continued dragging
      setDraggedField(updatedField);
      
      // Manually redraw overlays during drag to avoid full page refresh
      drawFieldOverlays();
    } else if (isDrawing && startPos) {
      // Handle field drawing preview
      drawPreview(startPos, coords);
    } else {
      // Handle cursor changes when hovering over fields
      const fieldAtPosition = getFieldAtCoordinates(coords.x, coords.y);
      
      if (fieldAtPosition) {
        // Check if hovering over a resize handle
        const handle = getResizeHandle(coords.x, coords.y, fieldAtPosition);
        
        if (handle && selectedFieldId === fieldAtPosition.id) {
          setCanvasCursor(getResizeCursor(handle));
        } else {
          setCanvasCursor('grab');
        }
        
        if (!hoveredField || hoveredField.id !== fieldAtPosition.id) {
          setHoveredField(fieldAtPosition);
        }
      } else {
        if (hoveredField) {
          setCanvasCursor('crosshair');
          setHoveredField(null);
        }
      }
    }
  };

  const handleCanvasMouseUp = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const coords = getCoordinates(e);
    if (!coords) return;
    
    if (isResizing) {
      // End resizing
      setIsResizing(false);
      setResizeField(null);
      setResizeHandle(null);
      setResizeStartPos(null);
      setResizeStartField(null);
      
      // Check if mouse is still over a field to set appropriate cursor
      const fieldAtPosition = getFieldAtCoordinates(coords.x, coords.y);
      if (fieldAtPosition) {
        const handle = getResizeHandle(coords.x, coords.y, fieldAtPosition);
        if (handle && selectedFieldId === fieldAtPosition.id) {
          setCanvasCursor(getResizeCursor(handle));
        } else {
          setCanvasCursor('grab');
        }
        setHoveredField(fieldAtPosition);
      } else {
        setCanvasCursor('crosshair');
        setHoveredField(null);
      }
      return;
    }
    
    if (isDragging) {
      // End dragging and check if we're still hovering over a field
      setIsDragging(false);
      setDraggedField(null);
      setDragOffset(null);
      
      // Check if mouse is still over a field to set appropriate cursor
      const fieldAtPosition = getFieldAtCoordinates(coords.x, coords.y);
      if (fieldAtPosition) {
        setCanvasCursor('grab');
        setHoveredField(fieldAtPosition);
      } else {
        setCanvasCursor('crosshair');
        setHoveredField(null);
      }
      return;
    }
    
    if (!isDrawing || !startPos) return;
    if (!coords) return;
    
    const endX = coords.x;
    const endY = coords.y;
    
    // Clear the overlay canvas
    clearPreview();
    
    const x = Math.min(startPos.x, endX);
    const y = Math.min(startPos.y, endY);
    const width = Math.abs(endX - startPos.x);
    const height = Math.abs(endY - startPos.y);
    
    // For checkboxes, use selected size; for text/images, use drawn size with minimum
    const fieldWidth = drawingMode === 'checkbox' ? checkboxSize : Math.max(width, 20);
    const fieldHeight = drawingMode === 'checkbox' ? checkboxSize : Math.max(height, 20);
    
    if (fieldWidth > 10 && fieldHeight > 10) {
      const newField: PDFField = {
        id: Date.now().toString(),
        name: drawingMode === 'image' ? (selectedImage?.name || t('editor.field.type.image.short')) : '',
        type: drawingMode,
        x,
        y,
        width: fieldWidth,
        height: fieldHeight,
        pageIndex: currentPage,
        fontSize: drawingMode === 'text' ? fontSize : undefined, // Only add fontSize for text fields
        checkboxSize: drawingMode === 'checkbox' ? checkboxSize : undefined, // Only add checkboxSize for checkbox fields
        imageData: drawingMode === 'image' ? pendingImageData || undefined : undefined,
        imageName: drawingMode === 'image' ? selectedImage?.name : undefined,
      };
      
      setCurrentField(newField);
      setFieldName(drawingMode === 'image' ? (selectedImage?.name || t('editor.field.type.image.short')) : '');
      
      // For images, save immediately without modal if we have image data
      if (drawingMode === 'image' && pendingImageData) {
        // Save image to history
        if (newField.imageData && newField.imageName) {
          addImageToHistory(newField.imageName, newField.imageData);
        }
        
        setFields(prev => [...prev, newField]);
        // Clear the pending image data
        setPendingImageData(null);
        setSelectedImage(null);
        setDrawingMode('text'); // Reset to text mode
      } else {
        setShowFieldModal(true);
      }
    }
    
    setIsDrawing(false);
    setStartPos(null);
  };

  // Touch event handlers for mobile support
  const handleCanvasTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault(); // Prevent scrolling while drawing
    const coords = getCoordinates(e);
    if (!coords) return;
    
    // Check if touching an existing field
    const fieldAtPosition = getFieldAtCoordinates(coords.x, coords.y);
    
    if (fieldAtPosition) {
      // First, ensure the field is selected
      const wasFieldSelected = selectedFieldId === fieldAtPosition.id;
      if (!wasFieldSelected) {
        setSelectedFieldId(fieldAtPosition.id);
      }
      
      // Check if touching a resize handle (works for both newly selected and already selected fields)
      const handle = getResizeHandle(coords.x, coords.y, fieldAtPosition);
      
      if (handle) {
        // Start resizing immediately
        setIsResizing(true);
        setResizeField(fieldAtPosition);
        setResizeHandle(handle);
        setResizeStartPos(coords);
        setResizeStartField({ ...fieldAtPosition });
        setCanvasCursor(getResizeCursor(handle));
      } else {
        // Start dragging the field
        setIsDragging(true);
        setDraggedField(fieldAtPosition);
        setCanvasCursor('grabbing');
        setDragOffset({
          x: coords.x - fieldAtPosition.x,
          y: coords.y - fieldAtPosition.y
        });
      }
    } else {
      // Start drawing a new field
      setStartPos(coords);
      setIsDrawing(true);
      setSelectedFieldId(null);
    }
  };

  const handleCanvasTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault(); // Prevent scrolling while drawing
    const coords = getCoordinates(e);
    if (!coords) return;
    
    if (isResizing && resizeField && resizeHandle && resizeStartPos && resizeStartField) {
      // Handle field resizing (same logic as mouse move)
      const deltaX = coords.x - resizeStartPos.x;
      const deltaY = coords.y - resizeStartPos.y;
      
      let newX = resizeStartField.x;
      let newY = resizeStartField.y;
      let newWidth = resizeStartField.width;
      let newHeight = resizeStartField.height;
      
      // Calculate new dimensions based on handle
      switch (resizeHandle) {
        case 'nw':
          newX = resizeStartField.x + deltaX;
          newY = resizeStartField.y + deltaY;
          newWidth = resizeStartField.width - deltaX;
          newHeight = resizeStartField.height - deltaY;
          break;
        case 'n':
          newY = resizeStartField.y + deltaY;
          newHeight = resizeStartField.height - deltaY;
          break;
        case 'ne':
          newY = resizeStartField.y + deltaY;
          newWidth = resizeStartField.width + deltaX;
          newHeight = resizeStartField.height - deltaY;
          break;
        case 'e':
          newWidth = resizeStartField.width + deltaX;
          break;
        case 'se':
          newWidth = resizeStartField.width + deltaX;
          newHeight = resizeStartField.height + deltaY;
          break;
        case 's':
          newHeight = resizeStartField.height + deltaY;
          break;
        case 'sw':
          newX = resizeStartField.x + deltaX;
          newWidth = resizeStartField.width - deltaX;
          newHeight = resizeStartField.height + deltaY;
          break;
        case 'w':
          newX = resizeStartField.x + deltaX;
          newWidth = resizeStartField.width - deltaX;
          break;
      }
      
      // Enforce minimum size
      const minSize = 10;
      if (newWidth < minSize) {
        if (resizeHandle.includes('w')) {
          newX = resizeStartField.x + resizeStartField.width - minSize;
        }
        newWidth = minSize;
      }
      if (newHeight < minSize) {
        if (resizeHandle.includes('n')) {
          newY = resizeStartField.y + resizeStartField.height - minSize;
        }
        newHeight = minSize;
      }
      
      // Update field
      const updatedField = { ...resizeField, x: newX, y: newY, width: newWidth, height: newHeight };
      
      if (resizeField.isExisting) {
        setExistingFields(prev => 
          prev.map(f => f.id === resizeField.id ? updatedField : f)
        );
      } else {
        setFields(prev => 
          prev.map(f => f.id === resizeField.id ? updatedField : f)
        );
      }
      
      // Update the resizeField reference for continued resizing
      setResizeField(updatedField);
      
      // Manually redraw overlays during resize
      drawFieldOverlays();
    } else if (isDragging && draggedField && dragOffset) {
      // Handle field dragging
      const newX = coords.x - dragOffset.x;
      const newY = coords.y - dragOffset.y;
      
      // Update field position
      const updatedField = { ...draggedField, x: newX, y: newY };
      
      if (draggedField.isExisting) {
        setExistingFields(prev => 
          prev.map(f => f.id === draggedField.id ? updatedField : f)
        );
      } else {
        setFields(prev => 
          prev.map(f => f.id === draggedField.id ? updatedField : f)
        );
      }
      
      // Update the draggedField reference for continued dragging
      setDraggedField(updatedField);
      
      // Manually redraw overlays during drag to avoid full page refresh
      drawFieldOverlays();
    } else if (isDrawing && startPos) {
      // Handle field drawing preview
      drawPreview(startPos, coords);
    }
  };

  const handleCanvasTouchEnd = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault(); // Prevent default touch behavior
    const coords = getCoordinates(e);
    if (!coords) return;
    
    if (isResizing) {
      // End resizing and reset cursor for mobile
      setIsResizing(false);
      setResizeField(null);
      setResizeHandle(null);
      setResizeStartPos(null);
      setResizeStartField(null);
      setCanvasCursor('crosshair'); // Reset to default for mobile
      return;
    }
    
    if (isDragging) {
      // End dragging and reset cursor for mobile
      setIsDragging(false);
      setDraggedField(null);
      setDragOffset(null);
      setCanvasCursor('crosshair'); // Reset to default for mobile
      return;
    }
    
    if (!isDrawing || !startPos) return;
    
    const endX = coords.x;
    const endY = coords.y;
    
    // Clear the overlay canvas
    clearPreview();
    
    const x = Math.min(startPos.x, endX);
    const y = Math.min(startPos.y, endY);
    const width = Math.abs(endX - startPos.x);
    const height = Math.abs(endY - startPos.y);
    
    // For checkboxes, use selected size; for text/images, use drawn size with minimum
    const fieldWidth = drawingMode === 'checkbox' ? checkboxSize : Math.max(width, 20);
    const fieldHeight = drawingMode === 'checkbox' ? checkboxSize : Math.max(height, 20);
    
    if (fieldWidth > 10 && fieldHeight > 10) {
      const newField: PDFField = {
        id: Date.now().toString(),
        name: drawingMode === 'image' ? (selectedImage?.name || t('editor.field.type.image.short')) : '',
        type: drawingMode,
        x,
        y,
        width: fieldWidth,
        height: fieldHeight,
        pageIndex: currentPage,
        fontSize: drawingMode === 'text' ? fontSize : undefined, // Only add fontSize for text fields
        checkboxSize: drawingMode === 'checkbox' ? checkboxSize : undefined, // Only add checkboxSize for checkbox fields
        imageData: drawingMode === 'image' ? pendingImageData || undefined : undefined,
        imageName: drawingMode === 'image' ? selectedImage?.name : undefined,
      };
      
      setCurrentField(newField);
      setFieldName(drawingMode === 'image' ? (selectedImage?.name || t('editor.field.type.image.short')) : '');
      
      // For images, save immediately without modal if we have image data
      if (drawingMode === 'image' && pendingImageData) {
        // Save image to history
        if (newField.imageData && newField.imageName) {
          addImageToHistory(newField.imageName, newField.imageData);
        }
        
        setFields(prev => [...prev, newField]);
        // Clear the pending image data
        setPendingImageData(null);
        setSelectedImage(null);
        setDrawingMode('text'); // Reset to text mode
      } else {
        setShowFieldModal(true);
      }
    }
    
    setIsDrawing(false);
    setStartPos(null);
  };

  const handleFieldSave = () => {
    if (!currentField) return;
    if (currentField.type !== 'image' && !fieldName.trim()) return; // Only require name for non-image fields
    
    const updatedField = { 
      ...currentField, 
      name: currentField.type === 'image' ? (currentField.imageName || t('editor.field.type.image.short')) : fieldName.trim(),
      fontSize: currentField.type === 'text' ? editFontSize : currentField.fontSize,
      checkboxSize: currentField.type === 'checkbox' ? editCheckboxSize : currentField.checkboxSize
    };

    // Save image to history if it's a new image field
    if (updatedField.type === 'image' && updatedField.imageData && !isEditMode) {
      addImageToHistory(updatedField.imageName || 'Image', updatedField.imageData);
    }
    
    if (isEditMode) {
      // Edit mode: update existing field
      if (currentField.isExisting) {
        // Update existing field
        setExistingFields(prev => 
          prev.map(f => f.id === currentField.id ? updatedField : f)
        );
      } else {
        // Update new field
        setFields(prev => 
          prev.map(f => f.id === currentField.id ? updatedField : f)
        );
      }
    } else {
      // Create mode: add new field
      setFields(prev => [...prev, updatedField]);
    }
    
    setShowFieldModal(false);
    setCurrentField(null);
    setFieldName('');
    setEditFontSize(fontSize);
    setEditCheckboxSize(checkboxSize);
    setIsEditMode(false);
    
    // Force redraw field overlays after saving - important for mobile touch
    setTimeout(() => {
      drawFieldOverlays();
    }, 100);
  };

  const handleFieldNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleFieldSave();
    }
  };

  const handleFieldDelete = (fieldId: string) => {
    setFields(prev => prev.filter(f => f.id !== fieldId));
    // Field overlays will be re-drawn automatically via useEffect
  };

  const handleExistingFieldDelete = (fieldId: string) => {
    setExistingFields(prev => prev.filter(f => f.id !== fieldId));
    setHasDeletedFields(true);
    // Field overlays will be re-drawn automatically via useEffect
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    // PDF rendering will be handled automatically via useEffect
  };

  const handleFieldSelect = (fieldId: string, pageIndex: number) => {
    setSelectedFieldId(fieldId);
    
    // Navigate to the correct page if needed
    if (pageIndex !== currentPage) {
      setCurrentPage(pageIndex);
    }
  };

  const handleFieldEdit = (fieldId: string) => {
    // Find the field in either new fields or existing fields
    const field = fields.find(f => f.id === fieldId) || existingFields.find(f => f.id === fieldId);
    if (field) {
      setCurrentField(field);
      setFieldName(field.name);
      setEditFontSize(field.fontSize || 12);
      setEditCheckboxSize(field.checkboxSize || checkboxSize);
      setIsEditMode(true);
      setShowFieldModal(true);
    }
  };

  const handleExistingFieldEdit = (fieldId: string) => {
    // This is the same as handleFieldEdit but kept separate for clarity
    handleFieldEdit(fieldId);
  };

  // Filter fields based on search term
  const filterFields = (fields: PDFField[]) => {
    if (!searchTerm.trim()) {
      return fields;
    }
    return fields.filter(field => 
      field.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const filteredFields = filterFields(fields);
  const filteredExistingFields = filterFields(existingFields);

  const savePDFWithFields = async () => {
    if (!pdfFile || !pdfDoc) return;
    
    try {
      // The solution: Create a new PDF and copy only the visual content
      // Then add only the fields we want (non-deleted existing fields + new fields)
      
      // Create a new PDF document 
      const newPdf = await PDFDocument.create();
      
      // Load the original PDF
      const originalPdf = await PDFDocument.load(await pdfFile.arrayBuffer());
      
      // Copy all pages from original PDF - this will copy visual content
      // The key is to clear any form fields after copying
      const pageCount = originalPdf.getPageCount();
      const pageIndices = Array.from({ length: pageCount }, (_, i) => i);
      const copiedPages = await newPdf.copyPages(originalPdf, pageIndices);
      
      // Add copied pages to new PDF
      copiedPages.forEach(page => newPdf.addPage(page));
      
      // Now we have the visual content but possibly also copied form fields
      // We need to clear the form structure to start fresh
      
      // Remove any AcroForm (form) that might have been copied
      // AND remove form field annotations from copied pages
      try {
        const context = newPdf.context;
        const catalog = context.lookup(newPdf.catalog);
        
        // 1. Remove the AcroForm entry from catalog to clear form structure
        if (catalog && typeof catalog === 'object' && 'get' in catalog && 'delete' in catalog) {
          const catalogObj = catalog as unknown as { get: (key: ReturnType<typeof PDFName.of>) => unknown; delete: (key: ReturnType<typeof PDFName.of>) => void };
          const acroFormRef = catalogObj.get(PDFName.of('AcroForm'));
          if (acroFormRef) {
            catalogObj.delete(PDFName.of('AcroForm'));
            console.log('Cleared AcroForm from PDF catalog');
          }
        }
        
        // 2. Remove form field annotations from ALL copied pages
        // This is crucial because copyPages also copies the form field widgets as page annotations
        for (let i = 0; i < newPdf.getPageCount(); i++) {
          const page = newPdf.getPage(i);
          const pageRef = page.ref;
          const pageDict = newPdf.context.lookup(pageRef);
          
          if (pageDict && typeof pageDict === 'object' && 'delete' in pageDict) {
            const pageObj = pageDict as unknown as { delete: (key: ReturnType<typeof PDFName.of>) => void };
            pageObj.delete(PDFName.of('Annots'));
            console.log(`Cleared annotations from page ${i}`);
          }
        }
        
        console.log('Successfully cleared all copied form fields and annotations');
      } catch (error) {
        console.warn('Could not clear copied form fields:', error);
      }
      
      // Create a fresh form for the new PDF
      const form = newPdf.getForm();
      
      // Add only the existing fields that weren't deleted
      console.log('Adding existing fields:', existingFields.length);
      for (const field of existingFields) {
        const page = newPdf.getPage(field.pageIndex);
        const { height: pageHeight } = page.getSize();
        const pdfY = pageHeight - field.y - field.height;
        
        try {
          if (field.type === 'text') {
            const textField = form.createTextField(field.name);
            textField.addToPage(page, {
              x: field.x,
              y: pdfY,
              width: field.width,
              height: field.height,
              borderColor: rgb(0, 0, 0),
              borderWidth: 0,
            });
            // Apply fontSize if specified
            if (field.fontSize) {
              textField.setFontSize(field.fontSize);
            }
          } else if (field.type === 'checkbox') {
            const checkboxField = form.createCheckBox(field.name);
            checkboxField.addToPage(page, {
              x: field.x,
              y: pdfY,
              width: field.width,
              height: field.height,
              borderColor: rgb(0, 0, 0),
              borderWidth: 1,
            });
          } else if (field.type === 'image' && field.imageData) {
            // Embed existing image directly to the page
            try {
              // Convert base64 to Uint8Array
              const base64Data = field.imageData.split(',')[1]; // Remove data:image/...;base64, prefix
              const imageBytes = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));
              
              // Determine image type and embed
              let embeddedImage;
              if (field.imageData.includes('data:image/png')) {
                embeddedImage = await newPdf.embedPng(imageBytes);
              } else if (field.imageData.includes('data:image/jpeg') || field.imageData.includes('data:image/jpg')) {
                embeddedImage = await newPdf.embedJpg(imageBytes);
              } else {
                // Try to embed as PNG by default
                embeddedImage = await newPdf.embedPng(imageBytes);
              }
              
              // Draw the image on the page
              page.drawImage(embeddedImage, {
                x: field.x,
                y: pdfY,
                width: field.width,
                height: field.height,
              });
            } catch (imageError) {
              console.error(`Error embedding existing image for field "${field.name}":`, imageError);
            }
          }
        } catch (error) {
          console.error(`Error adding existing field "${field.name}":`, error);
        }
      }
      
      // Group new fields by name to handle duplicates and synchronization
      const fieldGroups = new Map<string, PDFField[]>();
      fields.forEach(field => {
        if (!fieldGroups.has(field.name)) {
          fieldGroups.set(field.name, []);
        }
        fieldGroups.get(field.name)!.push(field);
      });
      
      console.log('Adding new field groups:', fieldGroups.size);
      
      // Create new fields with same names using the suggested approach
      for (const [fieldName, fieldList] of fieldGroups) {
        const createdFields: (import('pdf-lib').PDFTextField | import('pdf-lib').PDFCheckBox)[] = [];
        
        console.log(`Creating ${fieldList.length} fields with name "${fieldName}"`);
        
        // Create fields with temporary unique names first
        for (let i = 0; i < fieldList.length; i++) {
          const field = fieldList[i];
          const page = newPdf.getPage(field.pageIndex);
          const { height: pageHeight } = page.getSize();
          
          // Convert coordinates (canvas coordinates are top-left origin, PDF coordinates are bottom-left)
          const pdfY = pageHeight - field.y - field.height;
          
          // Use a completely unique temporary name to avoid conflicts
          const tempName = `temp_field_${Date.now()}_${Math.random().toString(36).substring(2, 9)}_${i}`;
          
          try {
            if (field.type === 'text') {
              // Create text field with temporary name
              const textField = form.createTextField(tempName);
              textField.addToPage(page, {
                x: field.x,
                y: pdfY,
                width: field.width,
                height: field.height,
                borderColor: rgb(0, 0, 0),
                borderWidth: 0,
              });
              // Apply fontSize if specified
              if (field.fontSize) {
                textField.setFontSize(field.fontSize);
              }
              createdFields.push(textField);
            } else if (field.type === 'checkbox') {
              // Create checkbox field with temporary name
              const checkboxField = form.createCheckBox(tempName);
              checkboxField.addToPage(page, {
                x: field.x,
                y: pdfY,
                width: field.width,
                height: field.height,
                borderColor: rgb(0, 0, 0),
                borderWidth: 1,
              });
              createdFields.push(checkboxField);
            } else if (field.type === 'image' && field.imageData) {
              // Embed image directly to the page
              try {
                // Convert base64 to Uint8Array
                const base64Data = field.imageData.split(',')[1]; // Remove data:image/...;base64, prefix
                const imageBytes = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));
                
                // Determine image type and embed
                let embeddedImage;
                if (field.imageData.includes('data:image/png')) {
                  embeddedImage = await newPdf.embedPng(imageBytes);
                } else if (field.imageData.includes('data:image/jpeg') || field.imageData.includes('data:image/jpg')) {
                  embeddedImage = await newPdf.embedJpg(imageBytes);
                } else {
                  // Try to embed as PNG by default
                  embeddedImage = await newPdf.embedPng(imageBytes);
                }
                
                // Draw the image on the page
                page.drawImage(embeddedImage, {
                  x: field.x,
                  y: pdfY,
                  width: field.width,
                  height: field.height,
                });
              } catch (imageError) {
                console.error(`Error embedding image for field "${field.name}":`, imageError);
              }
            }
          } catch (error) {
            console.error(`Error creating field with temp name "${tempName}":`, error);
          }
        }
        
        // Now rename all fields to have the same name (synchronize them)
        createdFields.forEach((field, index) => {
          try {
            const fieldRef = field.ref;
            const fieldDict = newPdf.context.lookup(fieldRef);
            
            // Set the same name for all fields to synchronize them
            if (fieldDict && typeof fieldDict === 'object' && 'set' in fieldDict) {
              // Use the exact fieldName without any modifications
              (fieldDict as unknown as { set: (key: import('pdf-lib').PDFName, value: import('pdf-lib').PDFString) => void }).set(PDFName.of('T'), PDFString.of(fieldName));
              console.log(`Renamed field ${index} to "${fieldName}"`);
            }
          } catch (error) {
            console.warn(`Could not rename field ${index} to "${fieldName}":`, error);
          }
        });
      }
      
      // Save the PDF with embedded fields
      const pdfBytes = await newPdf.save();
      
      // Download the PDF
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${pdfFile.name.replace('.pdf', '')}-with-fields.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      
      // Show success notification
      const totalFields = existingFields.length + fields.length;
      notifications.show({
        title: t('message.pdf.saved'),
        message: t('message.pdf.saved.detail').replace('{count}', totalFields.toString()),
        color: 'green',
        icon: <IconCheck size={18} />,
      });
      
    } catch (error) {
      console.error('Error saving PDF with fields:', error);
      notifications.show({
        title: t('message.pdf.save.error'),
        message: t('message.pdf.save.error.detail'),
        color: 'red',
        icon: <IconX size={18} />,
      });
    }
  };

  return (
    <Container size="xl">
      {(!isMobile || !pdfFile || isTopSectionExpanded) && (
        <Grid mb="xl" style={{ marginTop: !isMobile ? '2rem' : 0 }}>
          {/* Collapsed view when PDF is loaded */}
          {pdfFile && !isTopSectionExpanded && (
            <Grid.Col span={12}>
              <Paper p="md" withBorder>
                <Group justify="space-between" align="center">
                  <Group>
                    <div style={{ 
                      display: 'inline-flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      backgroundColor: 'var(--mantine-color-green-0)',
                      marginRight: '8px'
                    }}>
                      <IconCheck size={20} style={{ color: 'var(--mantine-color-green-6)' }} />
                    </div>
                    <div>
                      <Text size="sm" fw={500}>
                        {t('editor.upload.success')}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {pdfFile.name}
                      </Text>
                    </div>
                  </Group>
                  
                  <Group>
                    <FileButton
                      onChange={loadPDF}
                      accept=".pdf"
                      disabled={isLoading}
                    >
                      {(props) => (
                        <Button
                          {...props}
                          size="sm"
                          variant="light"
                          leftSection={<IconUpload size={16} />}
                          loading={isLoading}
                        >
                          {t('editor.upload.change')}
                        </Button>
                      )}
                    </FileButton>
                    
                    {!isMobile && (
                      <Button
                        size="sm"
                        variant="outline"
                        leftSection={<IconChevronDown size={16} />}
                        onClick={() => setIsTopSectionExpanded(true)}
                      >
                        {t('editor.instructions.show')}
                      </Button>
                    )}
                  </Group>
                </Group>
              </Paper>
            </Grid.Col>
          )}

          {/* Expanded view (default or when expanded) */}
          {(!pdfFile || isTopSectionExpanded) && (
            <>
              <Grid.Col span={{ base: 12, md: 8 }}>
                <Paper p="xl" withBorder style={{ height: '100%' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ marginBottom: '1.5rem' }}>
                      <div style={{ 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        width: '80px',
                        height: '80px',
                        borderRadius: '50%',
                        backgroundColor: 'var(--mantine-color-blue-0)',
                        marginBottom: '1rem'
                      }}>
                        <IconUpload size={40} style={{ color: 'var(--mantine-color-blue-6)' }} />
                      </div>
                      <Title order={3} mb="xs">
                        {!pdfFile ? t('editor.upload.button') : t('editor.upload.success')}
                      </Title>
                      <Text c="dimmed" size="sm">
                        {!pdfFile ? t('editor.upload.drag') : pdfFile.name}
                      </Text>
                    </div>

                    <FileButton
                      onChange={loadPDF}
                      accept=".pdf"
                      disabled={isLoading}
                    >
                      {(props) => (
                        <Button
                          {...props}
                          size="lg"
                          leftSection={<IconUpload size={20} />}
                          loading={isLoading}
                          variant={pdfFile ? "light" : "filled"}
                        >
                          {!pdfFile ? t('editor.upload.button') : t('editor.upload.change')}
                        </Button>
                      )}
                    </FileButton>
                    
                    {/* Show Projects button when no PDF is loaded but saved projects exist */}
                    {!pdfFile && hasSavedProjects && (
                      <Button
                        size="lg"
                        variant="light"
                        leftSection={<IconFolderOpen size={20} />}
                        onClick={handleLoadProjects}
                        ml="md"
                      >
                        {t('projects.button')}
                      </Button>
                    )}
                  </div>
                </Paper>
              </Grid.Col>

              {/* Instructions - Only show on desktop or when explicitly expanded on mobile */}
              {(!isMobile || isTopSectionExpanded) && (
                <Grid.Col span={{ base: 12, md: 4 }}>
                  <Paper p="xl" withBorder style={{ height: '100%' }}>
                    <Group justify="space-between" align="center" mb="md">
                      <Group>
                        <IconInfoCircle size={20} style={{ verticalAlign: 'middle' }} />
                        <Title order={4}>
                          {t('editor.instructions')}
                        </Title>
                      </Group>
                      
                      {pdfFile && (
                        <Button
                          size="xs"
                          variant="subtle"
                          leftSection={<IconChevronUp size={14} />}
                          onClick={() => setIsTopSectionExpanded(false)}
                        >
                          {t('editor.instructions.hide')}
                        </Button>
                      )}
                    </Group>
                    
                    <Stack gap="sm">
                      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                        <div style={{ 
                          minWidth: '24px', 
                          height: '24px', 
                          borderRadius: '50%', 
                          backgroundColor: 'var(--mantine-color-blue-6)',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          marginRight: '12px',
                          marginTop: '2px'
                        }}>
                          1
                        </div>
                        <Text size="sm">{t('editor.instructions.step1')}</Text>
                      </div>
                      
                      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                        <div style={{ 
                          minWidth: '24px', 
                          height: '24px', 
                          borderRadius: '50%', 
                          backgroundColor: 'var(--mantine-color-blue-6)',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          marginRight: '12px',
                          marginTop: '2px'
                        }}>
                          2
                        </div>
                        <Text size="sm">{t('editor.instructions.step2')}</Text>
                      </div>
                      
                      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                        <div style={{ 
                          minWidth: '24px', 
                          height: '24px', 
                          borderRadius: '50%', 
                          backgroundColor: 'var(--mantine-color-blue-6)',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          marginRight: '12px',
                          marginTop: '2px'
                        }}>
                          3
                        </div>
                        <Text size="sm">{t('editor.instructions.step3')}</Text>
                      </div>
                      
                      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                        <div style={{ 
                          minWidth: '24px', 
                          height: '24px', 
                          borderRadius: '50%', 
                          backgroundColor: 'var(--mantine-color-blue-6)',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          marginRight: '12px',
                          marginTop: '2px'
                        }}>
                          4
                        </div>
                        <Text size="sm">{t('editor.instructions.step4')}</Text>
                      </div>
                      
                      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                        <div style={{ 
                          minWidth: '24px', 
                          height: '24px', 
                          borderRadius: '50%', 
                          backgroundColor: 'var(--mantine-color-blue-6)',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          marginRight: '12px',
                          marginTop: '2px'
                        }}>
                          5
                        </div>
                        <Text size="sm">{t('editor.instructions.step5')}</Text>
                      </div>
                    </Stack>
                  </Paper>
                </Grid.Col>
              )}
            </>
          )}
        </Grid>
      )}

      {/* Controls section - Always visible when PDF is loaded */}
      {pdfJsDoc && (
        <Paper p="md" mb="xl" withBorder>
          <Stack gap="md">
            {/* Mobile-first layout: Compact horizontal layout on mobile */}
            {isMobile ? (
              /* Simplified single-row layout */
              <Group gap="xs" align="center" justify="space-between" wrap="nowrap">
                {/* Field type selector and size - grouped together */}
                <Group gap="xs">
                  <ActionIcon
                    variant={drawingMode === 'text' ? 'filled' : 'outline'}
                    size="md"
                    onClick={() => setDrawingMode('text')}
                    title={t('editor.field.type.text')}
                  >
                    <IconLetterCase size={18} />
                  </ActionIcon>
                  <ActionIcon
                    variant={drawingMode === 'checkbox' ? 'filled' : 'outline'}
                    size="md"
                    onClick={() => setDrawingMode('checkbox')}
                    title={t('editor.field.type.checkbox')}
                  >
                    <IconSquareCheck size={18} />
                  </ActionIcon>
                  <Group gap={0}>
                    <FileButton accept="image/*" onChange={handleImageSelect}>
                      {(props) => (
                        <ActionIcon
                          {...props}
                          variant={drawingMode === 'image' ? 'filled' : 'outline'}
                          size="md"
                          title={t('editor.image.add')}
                          style={{ 
                            borderTopRightRadius: 0, 
                            borderBottomRightRadius: 0,
                            borderRight: '1px solid var(--mantine-color-gray-4)'
                          }}
                        >
                          <IconPhoto size={18} />
                        </ActionIcon>
                      )}
                    </FileButton>
                    <ActionIcon
                      variant={drawingMode === 'image' ? 'filled' : 'outline'}
                      size="md"
                      title="Choisir dans l'historique"
                      onClick={handleOpenImageHistory}
                      style={{ 
                        borderTopLeftRadius: 0, 
                        borderBottomLeftRadius: 0 
                      }}
                    >
                      <IconList size={14} />
                    </ActionIcon>
                  </Group>
                  
                  {/* Size selector - placed right next to buttons */}
                  {drawingMode === 'text' && (
                    <Select
                      value={fontSize.toString()}
                      onChange={(value) => updateFontSize(parseInt(value || '12'))}
                      size="xs"
                      placeholder={t('editor.placeholder.font')}
                      w={60}
                      data={[
                        { value: '8', label: '8' },
                        { value: '10', label: '10' },
                        { value: '12', label: '12' },
                        { value: '14', label: '14' },
                        { value: '16', label: '16' },
                        { value: '18', label: '18' },
                        { value: '20', label: '20' },
                        { value: '24', label: '24' },
                      ]}
                    />
                  )}
                  
                  {drawingMode === 'checkbox' && (
                    <Select
                      value={checkboxSize.toString()}
                      onChange={(value) => updateCheckboxSize(parseInt(value || '12'))}
                      size="xs"
                      placeholder={t('editor.placeholder.size')}
                      w={60}
                      data={[
                        { value: '10', label: '10' },
                        { value: '12', label: '12' },
                        { value: '15', label: '15' },
                        { value: '20', label: '20' },
                        { value: '25', label: '25' },
                        { value: '30', label: '30' },
                        { value: '35', label: '35' },
                        { value: '40', label: '40' },
                      ]}
                    />
                  )}
                </Group>
                
                {/* Page navigation - compact version */}
                {totalPages > 1 && (
                  <Group gap="xs" align="center">
                    <ActionIcon
                      size="sm"
                      variant="outline"
                      onClick={() => handlePageChange(Math.max(0, currentPage - 1))}
                      disabled={currentPage === 0}
                    >
                      <IconChevronDown size={14} style={{ transform: 'rotate(90deg)' }} />
                    </ActionIcon>
                    <Text size="xs" style={{ minWidth: '40px', textAlign: 'center' }}>
                      {currentPage + 1}/{totalPages}
                    </Text>
                    <ActionIcon
                      size="sm"
                      variant="outline"
                      onClick={() => handlePageChange(Math.min(totalPages - 1, currentPage + 1))}
                      disabled={currentPage === totalPages - 1}
                    >
                      <IconChevronUp size={14} style={{ transform: 'rotate(90deg)' }} />
                    </ActionIcon>
                  </Group>
                )}
                
                {/* Action buttons */}
                <Group gap="xs">
                  <ActionIcon
                    variant="light"
                    size="md"
                    onClick={() => setShowFieldListOnMobile(!showFieldListOnMobile)}
                  >
                    <IconList size={18} />
                  </ActionIcon>
                  
                  {/* Project management buttons for mobile */}
                  <ActionIcon
                    variant="light"
                    size="md"
                    onClick={handleLoadProjects}
                    title={t('projects.modal.title')}
                  >
                    <IconFolderOpen size={18} />
                  </ActionIcon>
                  
                  {pdfFile && (
                    <ActionIcon
                      variant="light"
                      size="md"
                      onClick={handleSaveProject}
                      title={t('projects.save.modal.title')}
                    >
                      <IconDeviceFloppy size={18} />
                    </ActionIcon>
                  )}
                  
                  {(fields.length > 0 || hasDeletedFields) && (
                    <Button
                      onClick={savePDFWithFields}
                      variant="filled"
                      size="xs"
                      leftSection={<IconDownload size={16} />}
                    >
                      {t('editor.save.button.short')}
                    </Button>
                  )}
                </Group>
              </Group>
            ) : (
              /* Desktop layout - simplified single row */
              <Group gap="md" align="center" justify="space-between" wrap="nowrap">
                {/* Field type selector and size - grouped together */}
                <Group gap="sm">
                  <ActionIcon
                    variant={drawingMode === 'text' ? 'filled' : 'outline'}
                    size="lg"
                    onClick={() => setDrawingMode('text')}
                    title={t('editor.field.type.text')}
                  >
                    <IconLetterCase size={20} />
                  </ActionIcon>
                  <ActionIcon
                    variant={drawingMode === 'checkbox' ? 'filled' : 'outline'}
                    size="lg"
                    onClick={() => setDrawingMode('checkbox')}
                    title={t('editor.field.type.checkbox')}
                  >
                    <IconSquareCheck size={20} />
                  </ActionIcon>
                  <Group gap={0}>
                    <FileButton accept="image/*" onChange={handleImageSelect}>
                      {(props) => (
                        <ActionIcon
                          {...props}
                          variant={drawingMode === 'image' ? 'filled' : 'outline'}
                          size="lg"
                          title="Télécharger une nouvelle image"
                          style={{ 
                            borderTopRightRadius: 0, 
                            borderBottomRightRadius: 0,
                            borderRight: '1px solid var(--mantine-color-gray-4)'
                          }}
                        >
                          <IconPhoto size={20} />
                        </ActionIcon>
                      )}
                    </FileButton>
                    <ActionIcon
                      variant={drawingMode === 'image' ? 'filled' : 'outline'}
                      size="lg"
                      title="Choisir dans l'historique"
                      onClick={handleOpenImageHistory}
                      style={{ 
                        borderTopLeftRadius: 0, 
                        borderBottomLeftRadius: 0 
                      }}
                    >
                      <IconList size={16} />
                    </ActionIcon>
                  </Group>
                  
                  {/* Size selector - placed right next to buttons */}
                  {drawingMode === 'text' && (
                    <Select
                      value={fontSize.toString()}
                      onChange={(value) => updateFontSize(parseInt(value || '12'))}
                      size="sm"
                      placeholder={t('editor.placeholder.fontsize')}
                      w={90}
                      data={[
                        { value: '8', label: '8px' },
                        { value: '10', label: '10px' },
                        { value: '12', label: '12px' },
                        { value: '14', label: '14px' },
                        { value: '16', label: '16px' },
                        { value: '18', label: '18px' },
                        { value: '20', label: '20px' },
                        { value: '24', label: '24px' },
                      ]}
                    />
                  )}
                  
                  {drawingMode === 'checkbox' && (
                    <Select
                      value={checkboxSize.toString()}
                      onChange={(value) => updateCheckboxSize(parseInt(value || '12'))}
                      size="sm"
                      placeholder={t('editor.placeholder.size')}
                      w={80}
                      data={[
                        { value: '10', label: '10px' },
                        { value: '12', label: '12px' },
                        { value: '15', label: '15px' },
                        { value: '20', label: '20px' },
                        { value: '25', label: '25px' },
                        { value: '30', label: '30px' },
                        { value: '35', label: '35px' },
                        { value: '40', label: '40px' },
                      ]}
                    />
                  )}
                </Group>
                
                {/* Page navigation - compact icons */}
                {totalPages > 1 && (
                  <Group gap="xs" align="center">
                    <ActionIcon
                      size="md"
                      variant="outline"
                      onClick={() => handlePageChange(Math.max(0, currentPage - 1))}
                      disabled={currentPage === 0}
                      title={t('editor.page.previous')}
                    >
                      <IconChevronDown size={16} style={{ transform: 'rotate(90deg)' }} />
                    </ActionIcon>
                    <Text size="sm" style={{ minWidth: '60px', textAlign: 'center' }}>
                      {currentPage + 1}/{totalPages}
                    </Text>
                    <ActionIcon
                      size="md"
                      variant="outline"
                      onClick={() => handlePageChange(Math.min(totalPages - 1, currentPage + 1))}
                      disabled={currentPage === totalPages - 1}
                      title={t('editor.page.next')}
                    >
                      <IconChevronUp size={16} style={{ transform: 'rotate(90deg)' }} />
                    </ActionIcon>
                  </Group>
                )}
                
                {/* Project management and save buttons */}
                <Group gap="sm">
                  <Button
                    variant="light"
                    size="sm"
                    leftSection={<IconFolderOpen size={16} />}
                    onClick={handleLoadProjects}
                  >
                    {t('projects.button')}
                  </Button>
                  
                  {pdfFile && (
                    <Button
                      variant="light"
                      size="sm"
                      leftSection={<IconDeviceFloppy size={16} />}
                      onClick={handleSaveProject}
                    >
                      {t('projects.save.button')}
                    </Button>
                  )}
                  
                  {(fields.length > 0 || hasDeletedFields) && (
                    <Button
                      onClick={savePDFWithFields}
                      variant="filled"
                      size="sm"
                      leftSection={<IconDownload size={18} />}
                    >
                      {t('editor.save.button')}
                    </Button>
                  )}
                </Group>
              </Group>
            )}
          </Stack>
        </Paper>
      )}

      {/* PDF Canvas and Field List */}
      {pdfJsDoc && (
        <Grid>
          <Grid.Col span={{ base: 12, md: 8 }}>
            <Paper p="md">
              <div ref={containerRef} style={{ textAlign: 'center', position: 'relative', minHeight: '400px' }}>
                <canvas
                  ref={canvasRef}
                  style={{
                    border: '1px solid #ccc',
                    maxWidth: '100%',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    pointerEvents: 'auto',
                    touchAction: 'none', // Prevent default touch actions
                  }}
                />
                <canvas
                  ref={overlayCanvasRef}
                  onMouseDown={handleCanvasMouseDown}
                  onMouseMove={handleCanvasMouseMove}
                  onMouseUp={handleCanvasMouseUp}
                  onTouchStart={handleCanvasTouchStart}
                  onTouchMove={handleCanvasTouchMove}
                  onTouchEnd={handleCanvasTouchEnd}
                  style={{
                    border: '1px solid #ccc',
                    cursor: canvasCursor,
                    maxWidth: '100%',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    pointerEvents: 'auto',
                    touchAction: 'none', // Prevent default touch actions
                  }}
                />
              </div>
            </Paper>
          </Grid.Col>
          
          {/* Show field list conditionally on mobile */}
          {(!isMobile || showFieldListOnMobile) && (
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Paper p="md">
                <Title order={3} mb="md">
                  {t('editor.fields.title')} ({fields.length + existingFields.length})
                </Title>
              
              <TextInput
                placeholder={t('editor.fields.search.placeholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                leftSection={<IconSearch size={16} />}
                mb="md"
                styles={{
                  input: {
                    fontSize: '14px',
                  },
                }}
              />
              
              {/* Show new fields first */}
              {filteredFields.length > 0 && (
                <div>
                  <Text size="sm" fw={500} mb="xs" c="blue">
                    {t('editor.fields.new')} ({filteredFields.length}{searchTerm ? ` / ${fields.length}` : ''})
                  </Text>
                  <Stack gap="xs" mb="md">
                    {filteredFields.map((field) => (
                      <Card 
                        key={field.id} 
                        p="xs" 
                        withBorder 
                        style={{ 
                          cursor: 'pointer',
                          backgroundColor: selectedFieldId === field.id ? 'rgba(51, 154, 240, 0.1)' : undefined,
                          borderColor: selectedFieldId === field.id ? '#339af0' : undefined 
                        }}
                        onClick={() => handleFieldSelect(field.id, field.pageIndex)}
                      >
                        <Group justify="space-between">
                          <div>
                            <Text size="sm" fw={500}>
                              {field.name}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {field.type === 'text' ? t('editor.field.type.text.short') : 
                               field.type === 'checkbox' ? t('editor.field.type.checkbox.short') : 
                               t('editor.field.type.image.short')} - {t('editor.page.label')} {field.pageIndex + 1}
                              {field.type === 'text' && field.fontSize && (
                                <span> - {field.fontSize}px</span>
                              )}
                              {field.type === 'checkbox' && field.checkboxSize && (
                                <span> - {Math.round(field.checkboxSize)}px</span>
                              )}
                            </Text>
                          </div>
                          <Group gap="xs">
                            <ActionIcon
                              color="blue"
                              variant="light"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleFieldEdit(field.id);
                              }}
                              title={t('editor.field.edit')}
                            >
                              <IconEdit size={12} />
                            </ActionIcon>
                            <ActionIcon
                              color="red"
                              variant="light"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleFieldDelete(field.id);
                              }}
                            >
                              <IconTrash size={12} />
                            </ActionIcon>
                          </Group>
                        </Group>
                      </Card>
                    ))}
                  </Stack>
                </div>
              )}
              
              {filteredExistingFields.length > 0 && (
                <div>
                  <Text size="sm" fw={500} mb="xs" c="orange">
                    {t('editor.fields.existing')} ({filteredExistingFields.length}{searchTerm ? ` / ${existingFields.length}` : ''})
                  </Text>
                  <Stack gap="xs" mb="md">
                    {filteredExistingFields.map((field) => (
                      <Card 
                        key={field.id} 
                        p="xs" 
                        withBorder 
                        style={{ 
                          borderColor: selectedFieldId === field.id ? '#fd7e14' : '#fd7e14',
                          cursor: 'pointer',
                          backgroundColor: selectedFieldId === field.id ? 'rgba(253, 126, 20, 0.1)' : undefined
                        }}
                        onClick={() => handleFieldSelect(field.id, field.pageIndex)}
                      >
                        <Group justify="space-between">
                          <div>
                            <Text size="sm" fw={500} c="orange">
                              {field.name}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {field.type === 'text' ? t('editor.field.type.text.short') : 
                               field.type === 'checkbox' ? t('editor.field.type.checkbox.short') : 
                               t('editor.field.type.image.short')} - {t('editor.page.label')} {field.pageIndex + 1}
                              {field.type === 'text' && field.fontSize && (
                                <span> - {field.fontSize}px</span>
                              )}
                              {field.type === 'checkbox' && field.checkboxSize && (
                                <span> - {Math.round(field.checkboxSize)}px</span>
                              )}
                            </Text>
                          </div>
                          <Group gap="xs">
                            <Badge color="orange" variant="light" size="sm">
                              {t('editor.field.existing.badge')}
                            </Badge>
                            <ActionIcon
                              color="orange"
                              variant="light"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleExistingFieldEdit(field.id);
                              }}
                              title={t('editor.field.edit')}
                            >
                              <IconEdit size={12} />
                            </ActionIcon>
                            <ActionIcon
                              color="red"
                              variant="light"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleExistingFieldDelete(field.id);
                              }}
                            >
                              <IconTrash size={12} />
                            </ActionIcon>
                          </Group>
                        </Group>
                      </Card>
                    ))}
                  </Stack>
                </div>
              )}
              
              {filteredFields.length === 0 && filteredExistingFields.length === 0 && (
                <Text c="dimmed" size="sm">
                  {searchTerm ? t('editor.fields.search.noresults') : t('editor.fields.empty')}
                </Text>
              )}
            </Paper>
          </Grid.Col>
          )}
        </Grid>
      )}

      <Modal
        opened={showFieldModal}
        onClose={() => {
          setShowFieldModal(false);
          setCurrentField(null);
          setFieldName('');
          setEditFontSize(fontSize);
          setEditCheckboxSize(checkboxSize);
          setIsEditMode(false);
          // Redraw field overlays after cancelling
          setTimeout(() => {
            drawFieldOverlays();
          }, 100);
        }}
        title={isEditMode ? t('modal.field.title.edit') : t('modal.field.title')}
      >
        <Stack>
          {currentField && currentField.type !== 'image' && (
            <TextInput
              ref={fieldNameInputRef}
              label={t('modal.field.name.label')}
              placeholder={t('modal.field.name.placeholder')}
              value={fieldName}
              onChange={(e) => setFieldName(e.target.value)}
              onKeyDown={handleFieldNameKeyDown}
              data-autofocus
              required
            />
          )}
          
          {currentField && currentField.type === 'image' && (
            <div>
              <Text size="sm" fw={500}>{t('editor.image.added')}</Text>
              <Text size="xs" c="dimmed">
                {currentField.imageName || t('editor.image.unnamed')}
              </Text>
            </div>
          )}
          
          {currentField && currentField.type === 'text' && (
            <NumberInput
              label={t('editor.field.fontSize.label')}
              value={editFontSize}
              onChange={(value) => setEditFontSize(typeof value === 'number' ? value : 12)}
              min={8}
              max={72}
              step={1}
              suffix="px"
            />
          )}
          
          {currentField && currentField.type === 'checkbox' && (
            <NumberInput
              label={t('editor.field.checkboxSize.label')}
              value={editCheckboxSize}
              onChange={(value) => setEditCheckboxSize(typeof value === 'number' ? value : 20)}
              min={8}
              max={50}
              step={1}
              suffix="px"
            />
          )}
          
          {currentField && (
            <div>
              <Text size="sm" c="dimmed">
                {t('modal.field.type.label')} {currentField.type === 'text' ? t('modal.field.type.text.label') : 
                 currentField.type === 'checkbox' ? t('modal.field.type.checkbox.label') :
                 t('editor.field.type.image.short')}
              </Text>
              <Text size="sm" c="dimmed">
                {t('modal.field.position.label')} {Math.round(currentField.x)}, {Math.round(currentField.y)}
              </Text>
              <Text size="sm" c="dimmed">
                {t('modal.field.size.label')} {Math.round(currentField.width)} × {Math.round(currentField.height)}
              </Text>
            </div>
          )}
          
          <Group justify="flex-end">
            <Button
              variant="outline"
              onClick={() => {
                setShowFieldModal(false);
                setCurrentField(null);
                setFieldName('');
                setEditFontSize(fontSize);
                setEditCheckboxSize(checkboxSize);
                setIsEditMode(false);
                // Redraw field overlays after cancelling
                setTimeout(() => {
                  drawFieldOverlays();
                }, 100);
              }}
            >
              {t('modal.cancel')}
            </Button>
            <Button
              onClick={handleFieldSave}
              disabled={!fieldName.trim()}
            >
              {t('modal.save')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Image History Modal */}
      <ImageHistoryModal
        opened={showImageHistoryModal}
        onClose={() => setShowImageHistoryModal(false)}
        onImageSelect={handleImageSelectFromHistory}
      />

      {/* Save Project Modal */}
      <SaveProjectModal
        opened={showSaveProjectModal}
        onClose={() => {
          setShowSaveProjectModal(false);
          // Refresh saved projects state after closing the save modal
          refreshSavedProjectsState(); // async but no need to await
        }}
        pdfFile={pdfFile}
        fields={fields}
        existingFields={existingFields}
        currentPage={currentPage}
        totalPages={totalPages}
        defaultName={currentProjectName || undefined}
        currentProjectId={currentProjectId || undefined}
      />

      {/* Saved Projects Modal */}
      <SavedProjectsModal
        opened={showSavedProjectsModal}
        onClose={() => {
          setShowSavedProjectsModal(false);
          // Refresh saved projects state after closing the projects modal
          refreshSavedProjectsState(); // async but no need to await
        }}
        onLoadProject={handleLoadProject}
      />
    </Container>
  );
}